import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Users, ArrowRight, Coins, GraduationCap, Scale, X, List, Map as MapIcon, Search } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import CitiesMap from "@/components/app/CitiesMap";
import { useCityShortlist } from "@/hooks/useCityShortlist";
import PlaceReadinessCard from "@/components/PlaceReadinessCard";

const currencySymbol = (c) => ({
  GBP: "£", USD: "$", EUR: "€", CAD: "C$", AUD: "A$",
  NZD: "NZ$", SGD: "S$", HKD: "HK$", CHF: "CHF ", DKK: "kr",
  JPY: "¥", KRW: "₩", CNY: "¥", INR: "₹", AED: "AED ", CZK: "Kč", SEK: "kr",
}[c] || "");

// City Guides landing — 5 UK + 5 US cities.
// Grouped by country, each card is a link to /cities/:slug.
export default function Cities() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slugs: compareSlugs, toggle: toggleCompare, remove: removeSlug, clear: clearSlugs, cap: compareCap, full: compareFull } = useCityShortlist();
  const [view, setView] = useState("list"); // "list" | "map"
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/cities");
        if (!cancelled) setCities(res.data?.cities || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load cities");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const goCompare = () => {
    if (compareSlugs.length < 2) return;
    navigate(`/cities/compare?slugs=${compareSlugs.join(",")}`);
  };

  const uk = cities.filter((c) => c.country_code === "UK");
  const us = cities.filter((c) => c.country_code === "US");
  // Group everything that is neither UK nor US into "Europe" and "Rest of world" super-groups.
  const europeCodes = new Set(["DE", "FR", "NL", "IT", "ES", "IE", "DK", "CH", "AT", "CZ", "PT", "SE", "NO", "BE", "PL"]);
  const restCodes = new Set(["CA", "AU", "NZ", "SG", "HK", "JP", "KR", "CN", "IN", "AE", "TW", "IL"]);
  const europe = cities.filter((c) => europeCodes.has(c.country_code));
  const rest = cities.filter((c) => restCodes.has(c.country_code));

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title="Student City Guides — WhichUniversityAdvisor"
        description="Honest, editorial student guides to the top UK and US university cities. Cost of living, vibe, transport, and neighbourhoods."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="max-w-3xl">
          <div className="label-uppercase" data-testid="cities-eyebrow">City guides · Feb 2026</div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight">
            Will you actually enjoy living there?
          </h1>
          <p className="mt-5 text-stone-700 leading-relaxed max-w-2xl">
            Seventy editorial guides to the cities where our anchor universities sit — from
            London and Boston to Tokyo, Mumbai, Prague, Dubai, Tel Aviv, Lyon and Pittsburgh. Written
            from a student's-eye view: cost of living, neighbourhoods to consider, transport
            reality, and student quotes lifted straight from Reddit.
          </p>

          <CitySearch cities={cities} />

          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <Link
              to="/cities/matcher"
              data-testid="cities-matcher-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#7C3AED] text-white rounded-full text-sm font-medium hover:bg-[#6b2fd8] transition shadow-sm"
            >
              <Coins className="h-4 w-4" />
              Match cities to my budget
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>

            {/* View toggle — list is the editorial-first default; map is
                the "where in the world?" scan for families who don't yet
                know which country they want. */}
            <div
              role="tablist"
              aria-label="Switch between list and map view"
              data-testid="cities-view-toggle"
              className="inline-flex items-center rounded-full border border-stone-300 bg-white p-0.5 text-xs font-underground-sc tracking-wider"
            >
              <button
                type="button"
                role="tab"
                aria-selected={view === "list"}
                data-testid="cities-view-toggle-list"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                  view === "list"
                    ? "bg-[#002147] text-white"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                <List className="h-3 w-3" /> List
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "map"}
                data-testid="cities-view-toggle-map"
                onClick={() => setView("map")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                  view === "map"
                    ? "bg-[#002147] text-white"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                <MapIcon className="h-3 w-3" /> Map
              </button>
            </div>

            <span className="text-xs text-stone-500 self-center">
              338 cities · currency-aware · safety-graded
            </span>
          </div>
        </div>
      </section>

      {/* Industry hubs — 18 ranked landing pages */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="cities-industry-hubs">
        <div className="bg-white border border-stone-200 rounded-xl p-6 lg:p-8">
          <div className="flex items-baseline justify-between gap-4 mb-5">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7C3AED] font-semibold">
                Browse by industry
              </div>
              <h2 className="font-editorial text-2xl mt-1 text-[#002147]">
                Ranked lists: which cities lead each sector?
              </h2>
            </div>
            <span className="text-xs text-stone-500 hidden md:inline">
              18 hubs · sourced hub ratings
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              { tag: "law",         label: "Law",              blurb: "Magic Circle firms" },
              { tag: "finance",     label: "Finance",          blurb: "Banking centres" },
              { tag: "tech",        label: "Tech",             blurb: "Software & AI" },
              { tag: "healthcare",  label: "Healthcare",       blurb: "Bio & pharma" },
              { tag: "engineering", label: "Engineering",      blurb: "Hardware hubs" },
              { tag: "startups",    label: "Startups",         blurb: "Startup Genome" },
              { tag: "creative",    label: "Creative",         blurb: "Media & design" },
              { tag: "research",    label: "Research",         blurb: "Top universities" },
              { tag: "government",  label: "Government",       blurb: "Policy capitals" },
              { tag: "fintech",     label: "Fintech",          blurb: "Payments & crypto" },
              { tag: "biotech",     label: "Biotech",          blurb: "Life-sciences R&D" },
              { tag: "aerospace",   label: "Aerospace",        blurb: "Aviation & space" },
              { tag: "cleantech",   label: "Cleantech",        blurb: "Climate & energy" },
              { tag: "gaming",      label: "Gaming & Media",   blurb: "Studios & streaming" },
              { tag: "ecommerce",   label: "Ecommerce",        blurb: "Marketplaces" },
              { tag: "mobility",    label: "Mobility",         blurb: "Logistics & AV" },
              { tag: "agritech",    label: "AgriTech",         blurb: "Food & agriculture" },
              { tag: "edtech",      label: "EdTech",           blurb: "Education tech" },
            ].map((h) => (
              <Link
                key={h.tag}
                to={`/cities/hubs/${h.tag}`}
                data-testid={`cities-hub-${h.tag}`}
                className="group border border-stone-200 rounded-lg p-3 hover:border-[#7C3AED] hover:shadow-sm transition"
              >
                <div className="text-sm font-medium text-[#002147] group-hover:text-[#7C3AED]">
                  {h.label}
                </div>
                <div className="text-[10px] text-stone-500 mt-0.5">{h.blurb}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {loading && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="cities-loading">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-56 rounded-xl bg-stone-100 animate-pulse" />
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-3xl mx-auto px-6 py-8 text-red-700" data-testid="cities-error">
          Something went wrong loading the city guides: {error}
        </div>
      )}

      {/* Place-pillar readiness — auto-hides when no cities have been
          shortlisted yet. Shows the "how many more to unlock Prove"
          nudge in a compact card once the student adds anything. */}
      {compareSlugs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
          <PlaceReadinessCard testId="cities-place-readiness" />
        </section>
      )}

      {!loading && !error && view === "map" && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
          <CitiesMap cities={cities} />
        </div>
      )}

      {!loading && !error && view === "list" && (
        <>
          <CountrySection
            title="United Kingdom"
            cities={uk}
            testId="cities-uk-section"
            compareSlugs={compareSlugs}
            onToggleCompare={toggleCompare}
          />
          <CountrySection
            title="United States"
            cities={us}
            testId="cities-us-section"
            compareSlugs={compareSlugs}
            onToggleCompare={toggleCompare}
          />
          <CountrySection
            title="Europe"
            cities={europe}
            testId="cities-europe-section"
            compareSlugs={compareSlugs}
            onToggleCompare={toggleCompare}
          />
          <CountrySection
            title="Canada, Australia & Asia-Pacific"
            cities={rest}
            testId="cities-rest-section"
            compareSlugs={compareSlugs}
            onToggleCompare={toggleCompare}
          />
          <div className="h-24" />
        </>
      )}

      {/* Sticky compare tray */}
      {compareSlugs.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-[#002147] text-white shadow-2xl rounded-full px-5 py-3 flex items-center gap-4 border border-stone-200" data-testid="cities-compare-tray">
          <div className="flex items-center gap-2 text-sm">
            <Scale className="h-4 w-4" />
            <span className="font-semibold">{compareSlugs.length}</span>
            <span className="text-white/70">/ 4 selected{compareSlugs.length >= 4 ? " (max)" : ""}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 max-w-[300px]">
            {compareSlugs.map((s) => (
              <button
                key={s}
                onClick={() => toggleCompare(s)}
                className="text-[10px] uppercase tracking-wider bg-white/10 hover:bg-white/20 rounded-full px-2 py-0.5 flex items-center gap-1"
                data-testid={`compare-tray-remove-${s}`}
              >
                {s} <X className="h-2.5 w-2.5" />
              </button>
            ))}
          </div>
          <Button
            onClick={goCompare}
            disabled={compareSlugs.length < 2}
            data-testid="cities-compare-go"
            className="bg-white text-[#002147] hover:bg-stone-100 disabled:opacity-50 rounded-full text-xs h-8 px-4"
          >
            Compare
          </Button>
        </div>
      )}
    </div>
  );
}

// Normalise diacritics + case so "munchen" matches "München" and vice versa.
function _norm(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function CitySearch({ cities }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click.
  useEffect(() => {
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const matches = useMemo(() => {
    const needle = _norm(q);
    if (!needle) return [];
    // Rank: exact-name > name-startsWith > name-contains > country-contains.
    const scored = [];
    for (const c of cities || []) {
      const n = _norm(c.name);
      const co = _norm(c.country);
      let score = -1;
      if (n === needle) score = 0;
      else if (n.startsWith(needle)) score = 1;
      else if (n.includes(needle)) score = 2;
      else if (co.includes(needle)) score = 3;
      if (score >= 0) scored.push({ c, score });
    }
    scored.sort((a, b) =>
      a.score !== b.score ? a.score - b.score : a.c.name.localeCompare(b.c.name)
    );
    return scored.slice(0, 8).map((s) => s.c);
  }, [q, cities]);

  const go = (city) => {
    if (!city) return;
    setOpen(false);
    setQ("");
    navigate(`/cities/${city.slug}`);
  };

  const onKey = (e) => {
    if (!open || !matches.length) {
      if (e.key === "ArrowDown" && matches.length) {
        setOpen(true);
        setCursor(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(matches[cursor]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const total = (cities || []).length;

  return (
    <div ref={wrapRef} className="mt-7 relative max-w-2xl" data-testid="cities-search">
      <div className="relative">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setCursor(0);
          }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKey}
          placeholder={total ? `Search ${total} cities — try “Dubai”, “Prague”, “Tokyo”…` : "Search cities…"}
          aria-label="Search cities"
          aria-autocomplete="list"
          aria-expanded={open && matches.length > 0}
          data-testid="cities-search-input"
          className="w-full h-14 pl-14 pr-12 rounded-full border border-stone-300 bg-white text-base text-stone-900 placeholder:text-stone-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40 focus:border-[#7C3AED] transition"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setOpen(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            data-testid="cities-search-clear"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && q && (
        <div
          role="listbox"
          data-testid="cities-search-results"
          className="absolute left-0 right-0 mt-2 bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden z-30"
        >
          {matches.length === 0 ? (
            <div
              className="px-5 py-4 text-sm text-stone-500"
              data-testid="cities-search-empty"
            >
              No cities match “{q}”. Try a country or a shorter prefix.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {matches.map((c, i) => (
                <li
                  key={c.slug}
                  role="option"
                  aria-selected={i === cursor}
                  data-testid={`cities-search-result-${c.slug}`}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    go(c);
                  }}
                  className={`px-5 py-3 flex items-center justify-between gap-4 cursor-pointer ${
                    i === cursor ? "bg-stone-100" : "hover:bg-stone-50"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#002147] truncate">
                      {c.name}
                    </div>
                    <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" />
                      {c.country}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-stone-300 shrink-0" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CountrySection({ title, cities, testId, compareSlugs, onToggleCompare }) {
  if (!cities.length) return null;
  const capReached = compareSlugs.length >= 4;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-14" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-editorial text-2xl sm:text-3xl">{title}</h2>
        <div className="text-xs text-stone-500 tracking-wider uppercase">
          {cities.length} cities
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cities.map((c) => (
          <CityCard
            key={c.slug}
            city={c}
            selected={compareSlugs.includes(c.slug)}
            onToggleCompare={onToggleCompare}
            disabledForCompare={capReached}
          />
        ))}
      </div>
    </section>
  );
}

function CityCard({ city, selected, onToggleCompare, disabledForCompare }) {
  const cur = city.cost_of_living?.currency;
  const lo = city.cost_of_living?.student_budget_low;
  const mid = city.cost_of_living?.student_budget_mid;
  const showDisabled = disabledForCompare && !selected;
  return (
    <div className="relative">
      {/* Compare checkbox — absolute-positioned on top of the link */}
      <label
        className={`absolute top-3 right-3 z-10 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-white/90 backdrop-blur border border-stone-200 rounded-full px-2 py-1 hover:bg-white ${
          showDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`city-compare-toggle-${city.slug}`}
        title={showDisabled ? "You can compare up to 4 cities at once" : ""}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleCompare(city.slug)}
          disabled={showDisabled}
          className="h-3 w-3 accent-[#002147]"
        />
        Compare
      </label>
      <Link
        to={`/cities/${city.slug}`}
        data-testid={`city-card-${city.slug}`}
        className={`group relative bg-white border rounded-xl p-6 hover:shadow-lg transition-all block ${
          selected ? "border-[#002147] shadow-md" : "border-stone-200 hover:border-[#002147]"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-stone-500 uppercase tracking-wider">
              <MapPin className="h-3 w-3" />
              {city.country}
            </div>
            <h3 className="font-editorial text-2xl mt-1.5 group-hover:text-[#002147]">
              {city.name}
            </h3>
          </div>
          <ArrowRight className="h-5 w-5 text-stone-300 group-hover:text-[#002147] group-hover:translate-x-1 transition-transform" />
        </div>

        <p className="mt-3 text-sm text-stone-700 leading-relaxed line-clamp-3">
          {city.vibe}
        </p>

        <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="flex items-center gap-1 text-stone-500 uppercase tracking-wider">
              <Users className="h-3 w-3" /> Students
            </div>
            <div className="font-mono text-sm text-stone-900 mt-0.5">
              {city.student_population
                ? `${Math.round(city.student_population / 1000)}k`
                : "—"}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-stone-500 uppercase tracking-wider">
              <Coins className="h-3 w-3" /> Monthly budget
            </div>
            <div className="font-mono text-sm text-stone-900 mt-0.5">
              {lo && mid ? `${currencySymbol(cur)}${lo.toLocaleString()}–${mid.toLocaleString()}` : "—"}
            </div>
          </div>
        </div>

        {(city.universities_in_city_count || city.featured_universities?.length) > 0 && (
          <div className="mt-4 flex items-center gap-1.5 text-xs text-stone-500">
            <GraduationCap className="h-3 w-3" />
            {city.universities_in_city_count || city.featured_universities.length} {(city.universities_in_city_count || city.featured_universities.length) === 1 ? "university" : "universities"} covered
          </div>
        )}
      </Link>
    </div>
  );
}
